/**
 * Sekme değiştirici (#476) — SSMS / Visual Studio'daki Ctrl+Tab penceresinin karşılığı.
 * Alt+Q basılı tutulunca açık ekranlar son kullanılan sırasıyla listelenir; Q (Shift+Q geri),
 * ok tuşları ya da fare ile seçilir, Alt bırakılınca ya da Enter ile geçilir, Esc vazgeçer.
 * Diğer sekme kısayolları da burada dinlenir (bkz. lib/tabKeys).
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Home } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import { NavIcon } from '../../navMenu/NavIcon';
import { useTabsStore, tabLabel, type TabModel } from '../tabsStore';
import { mruOrder, stepTab, tabCommandOf } from '../lib/tabKeys';

const standalone = () =>
  typeof window !== 'undefined' && !!window.matchMedia?.('(display-mode: standalone)').matches;

export function TabSwitcher() {
  const { t } = useTranslation();
  const [list, setList] = useState<TabModel[] | null>(null);
  const [index, setIndex] = useState(0);
  const holdKey = useRef<'Alt' | 'Control'>('Alt');
  const state = useRef({ list, index });
  useEffect(() => {
    state.current = { list, index };
  });

  useEffect(() => {
    const store = useTabsStore.getState;
    const commit = () => {
      const { list: l, index: i } = state.current;
      const target = l?.[i];
      setList(null);
      if (target) store().activate(target.tabId);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      // Açık listede gezinme
      if (state.current.list) {
        const n = state.current.list.length;
        if (e.key === 'Escape') {
          e.preventDefault();
          setList(null);
          return;
        }
        if (e.key === 'Enter') {
          e.preventDefault();
          commit();
          return;
        }
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          e.preventDefault();
          setIndex((i) => (i + (e.key === 'ArrowDown' ? 1 : -1) + n) % n);
          return;
        }
      }
      const cmd = tabCommandOf(e, standalone());
      if (!cmd) return;
      const { tabs, activeTabId } = store();
      e.preventDefault();
      e.stopPropagation();
      switch (cmd.type) {
        case 'next':
        case 'prev':
          store().activate(stepTab(tabs, activeTabId, cmd.type === 'next' ? 1 : -1));
          return;
        case 'nth': {
          const tab = tabs[cmd.index];
          if (tab) store().activate(tab.tabId);
          return;
        }
        case 'home':
          store().activate(null);
          return;
        case 'close':
          if (activeTabId) store().close(activeTabId);
          return;
        case 'switcher': {
          holdKey.current = e.ctrlKey ? 'Control' : 'Alt';
          const cur = state.current.list;
          if (cur) {
            const n = cur.length;
            setIndex((i) => (i + (cmd.back ? -1 : 1) + n) % n);
            return;
          }
          const ordered = mruOrder(tabs);
          if (!ordered.length) return;
          setList(ordered);
          // İlk basışta bir önceki sekme seçili gelir (en sık ihtiyaç: iki ekran arasında gidip gelmek).
          const activeIdx = ordered.findIndex((x) => x.tabId === activeTabId);
          setIndex(ordered.length > 1 ? (activeIdx === 0 ? 1 : 0) : 0);
        }
      }
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (state.current.list && e.key === holdKey.current) {
        // Windows'ta tek başına bırakılan Alt tarayıcı menüsüne odaklanıyordu.
        e.preventDefault();
        commit();
      }
    };
    const onBlur = () => setList(null);
    window.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('keyup', onKeyUp, true);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('keydown', onKeyDown, true);
      window.removeEventListener('keyup', onKeyUp, true);
      window.removeEventListener('blur', onBlur);
    };
  }, []);

  if (!list) return null;
  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/20"
      onMouseDown={() => setList(null)}
    >
      <div
        role="listbox"
        aria-label={t('tabs.switcher.title')}
        className="border-border bg-bg-elevated w-[min(420px,92vw)] rounded-lg border p-2 shadow-[var(--shadow)]"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="text-fg-subtle px-2 pb-1 text-xs font-medium">{t('tabs.switcher.title')}</div>
        <ul className="max-h-[60vh] overflow-y-auto">
          {list.map((tab, i) => (
            <li
              key={tab.tabId}
              role="option"
              aria-selected={i === index}
              onMouseEnter={() => setIndex(i)}
              onClick={() => {
                setList(null);
                useTabsStore.getState().activate(tab.tabId);
              }}
              className={cn(
                'flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm',
                i === index ? 'bg-brand-500/15 text-brand-700 dark:text-brand-300' : 'hover:bg-bg-hover',
              )}
            >
              <NavIcon
                node={{
                  iconId: tab.iconId ?? null,
                  screenTypeName: tab.screenTypeName ?? null,
                  children: [],
                }}
                className="h-4 w-4 shrink-0"
              />
              <span className="min-w-0 flex-1 truncate">{tabLabel(tab)}</span>
            </li>
          ))}
        </ul>
        <div className="border-border text-fg-subtle mt-1 flex flex-wrap gap-x-3 gap-y-0.5 border-t px-2 pt-1.5 text-[11px]">
          <span>{t('tabs.switcher.hintSwitch')}</span>
          <span>{t('tabs.switcher.hintStep')}</span>
          <span>{t('tabs.switcher.hintNth')}</span>
          <span className="inline-flex items-center gap-1">
            <Home className="h-3 w-3" /> {t('tabs.switcher.hintHome')}
          </span>
          <span>{t('tabs.switcher.hintClose')}</span>
        </div>
      </div>
    </div>
  );
}
